import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { TenantInput, TenantProfile } from '../../../models/userManagement';
import TenantDialog from '../dialogs/TenantDialog';

interface TenantsTabProps {
  tenants: TenantProfile[];
  loading: boolean;
  canEdit: boolean;
  onCreateTenant: (payload: TenantInput) => Promise<void> | void;
  onUpdateTenant: (id: string, payload: TenantInput) => Promise<void> | void;
}

const TenantsTab: React.FC<TenantsTabProps> = ({ tenants, loading, canEdit, onCreateTenant, onUpdateTenant }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantProfile | undefined>(undefined);

  const sortedTenants = useMemo(
    () => [...tenants].sort((a, b) => a.name.localeCompare(b.name)),
    [tenants]
  );

  const handleNewTenant = () => {
    setEditingTenant(undefined);
    setDialogOpen(true);
  };

  const handleEditTenant = (tenant: TenantProfile) => {
    setEditingTenant(tenant);
    setDialogOpen(true);
  };

  const handleSubmit = async (payload: TenantInput) => {
    if (editingTenant) {
      await onUpdateTenant(editingTenant.id, payload);
    } else {
      await onCreateTenant(payload);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Tenants</Typography>
        {canEdit && (
          <Button variant="contained" onClick={handleNewTenant}>
            Create Tenant
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Default Model Type</TableCell>
                <TableCell>Allowed Model Types</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Depots</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTenants.map((tenant) => (
                <TableRow key={tenant.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{tenant.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tenant.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tenant.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={tenant.active ? 'success' : 'default'}
                      variant={tenant.active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{tenant.defaultModelType}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {tenant.allowedModelTypes.map((model) => (
                        <Chip key={model} label={model} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{tenant.userCount ?? '—'}</TableCell>
                  <TableCell>{tenant.depotCount ?? '—'}</TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <Tooltip title="Edit tenant">
                        <IconButton size="small" onClick={() => handleEditTenant(tenant)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {sortedTenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 7 : 6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No tenants available.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TenantDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialValues={editingTenant}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};

export default TenantsTab;
